import { useState, useEffect, useCallback, useRef } from 'react';

// Notification sound (short beep)
const NOTIFICATION_SOUND_URL = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbsGYkIjCLv+HYqWA5PlWlw9LRqGdCQkSTosHKsHZIQkyCl7e4rnpPS0l6i6ivqH9ZUk5vfJOamoxkWVNlcYGKioBqYFlgaXN5dnRpYV1hZGlpa2xsaGVjY2VoaWprampoZ2doaGhoaWlqamtra2tqampqampra2tsbGxra2pqampqamtra2xsbGtra2pqampqamtra2xsbGtra2pqampqamtra2xsbGtra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2tra2trAAAA';

/**
 * Custom hook for browser push notifications
 * Handles permission requests, notification display, and localStorage preferences
 */
const usePushNotifications = () => {
  const [permission, setPermission] = useState('default');
  const [isEnabled, setIsEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const previousCountRef = useRef(null);
  const audioRef = useRef(null);

  // Check if browser supports notifications
  const isSupported = typeof window !== 'undefined' && 'Notification' in window;

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
    audioRef.current.volume = 0.5;
  }, []);

  // Initialize permission state
  useEffect(() => {
    if (isSupported) {
      setPermission(Notification.permission);
      // Load preferences from localStorage
      const savedPref = localStorage.getItem('dm_notifications_enabled');
      const savedSound = localStorage.getItem('dm_notification_sound');
      setIsEnabled(savedPref === 'true' && Notification.permission === 'granted');
      setSoundEnabled(savedSound !== 'false');
    }
  }, [isSupported]);

  // Play notification sound
  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, [soundEnabled]);

  // Toggle sound
  const toggleSound = useCallback(() => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('dm_notification_sound', newValue.toString());
    return newValue;
  }, [soundEnabled]);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      console.warn('Browser does not support notifications');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        setIsEnabled(true);
        localStorage.setItem('dm_notifications_enabled', 'true');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      return false;
    }
  }, [isSupported]);

  // Toggle notifications on/off
  const toggleNotifications = useCallback(async () => {
    if (isEnabled) {
      // Disable notifications
      setIsEnabled(false);
      localStorage.setItem('dm_notifications_enabled', 'false');
      return false;
    } else {
      // Try to enable notifications
      if (permission === 'granted') {
        setIsEnabled(true);
        localStorage.setItem('dm_notifications_enabled', 'true');
        return true;
      } else if (permission === 'default') {
        return await requestPermission();
      } else {
        // Permission denied - user needs to enable in browser settings
        return false;
      }
    }
  }, [isEnabled, permission, requestPermission]);

  // Show a browser notification
  const showNotification = useCallback((title, options = {}) => {
    if (!isSupported || !isEnabled || permission !== 'granted') {
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: options.tag || 'slidecon-notification',
        renotify: true,
        ...options
      });

      // Play sound
      playSound();

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        if (options.onClick) {
          options.onClick();
        }
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);

      return notification;
    } catch (error) {
      console.error('Failed to show notification:', error);
      return null;
    }
  }, [isSupported, isEnabled, permission, playSound]);

  // Check for new messages and show notification
  const checkAndNotify = useCallback((currentCount, senderName = 'Someone') => {
    if (previousCountRef.current !== null && currentCount > previousCountRef.current) {
      const newMessages = currentCount - previousCountRef.current;
      showNotification(
        `New Message${newMessages > 1 ? 's' : ''}`,
        {
          body: newMessages > 1 
            ? `You have ${newMessages} new messages` 
            : `${senderName} sent you a message`,
          tag: 'dm-notification',
          onClick: () => {
            window.location.href = '/messages';
          }
        }
      );
    }
    previousCountRef.current = currentCount;
  }, [showNotification]);

  return {
    isSupported,
    permission,
    isEnabled,
    soundEnabled,
    requestPermission,
    toggleNotifications,
    toggleSound,
    showNotification,
    checkAndNotify,
    playSound
  };
};

export default usePushNotifications;
